import { CloseIcon, EmptyBoxImage } from '@approvalmax/ui';
import { Box, Button, Flex, NoContent, Table } from '@approvalmax/ui/src/components';
import { domain } from 'modules/data';
import { useDispatch } from 'modules/react-redux';
import { reloadRequestList } from 'pages/requestList/actions';
import { memo, useCallback, useMemo } from 'react';
import {
    useInvalidateGetNetSuiteBillMatching,
    useInvalidateGetNetSuitePurchaseOrderMatching,
} from 'shared/data/webApp/v2';
import { useNetSuiteLineFieldSettings, useNetSuiteMatchingLines } from 'shared/hooks/(matchings)';
import { useNetSuiteCompanyCustomFields, useNetSuiteNewLineCustomFieldsDefinitions } from 'shared/hooks/(requests)';

import { Footer } from '../Footer/Footer';
import { getColumnDefinitions } from './Content.config';
import { useDeleteMatching } from './Content.hooks';
import { messages } from './Content.messages';
import { ContentProps } from './Content.types';

export const Content = memo((props: ContentProps) => {
    const { request, lineType, lineNumber, matchingInfo, isFetching, onDropdownClose } = props;

    const { matchingLinesInfos } = matchingInfo;

    const dispatch = useDispatch();

    const { deleteMatching, isLoadingDeleteMatching } = useDeleteMatching(lineType);

    const invalidateGetNetSuiteBillMatching = useInvalidateGetNetSuiteBillMatching();
    const invalidateGetNetSuitePurchaseOrderMatching = useInvalidateGetNetSuitePurchaseOrderMatching();

    const { netSuiteCompanyCustomFields } = useNetSuiteCompanyCustomFields(request.companyId);

    const matchedLinesIntegrationCode =
        request.integrationCode === domain.IntegrationCode.NetSuiteBill
            ? domain.IntegrationCode.NetSuitePO
            : domain.IntegrationCode.NetSuiteBill;

    const fieldSettings = useNetSuiteLineFieldSettings(request.companyId, matchedLinesIntegrationCode, lineType);

    const lines = useNetSuiteMatchingLines({
        matchingLinesInfos,
        lineType,
        lineNumber,
        request,
    });

    const customFieldsColumnDefinition = useNetSuiteNewLineCustomFieldsDefinitions(lines, netSuiteCompanyCustomFields);

    const handleMatchingRemove = useCallback(
        (lineNumber: number | undefined, requestId: string) => {
            deleteMatching(
                {
                    params: {
                        path: {
                            companyId: request.companyId,
                            requestId,
                            lineNumber,
                        },
                    },
                },
                {
                    onSuccess: () => {
                        invalidateGetNetSuiteBillMatching({
                            path: {
                                companyId: request.companyId,
                                vendorBillRequestId: requestId,
                            },
                        });
                        invalidateGetNetSuitePurchaseOrderMatching({
                            path: {
                                companyId: request.companyId,
                                purchaseOrdersRequestId: requestId,
                            },
                        });
                        dispatch(reloadRequestList());
                        onDropdownClose();
                    },
                }
            );
        },
        [
            request.companyId,
            deleteMatching,
            onDropdownClose,
            dispatch,
            invalidateGetNetSuiteBillMatching,
            invalidateGetNetSuitePurchaseOrderMatching,
        ]
    );

    const columnDefinitions = useMemo(
        () => [
            ...getColumnDefinitions({
                request,
                lineType,
                fieldSettings,
                isLoadingDeleteMatching,
                handleMatchingRemove,
            }),
            ...customFieldsColumnDefinition,
        ],
        [request, lineType, fieldSettings, customFieldsColumnDefinition, isLoadingDeleteMatching, handleMatchingRemove]
    );

    return (
        <Box spacing='24'>
            <Flex justifyContent='end'>
                <Button icon noPadding onClick={onDropdownClose} variant='text' color='blue100'>
                    <CloseIcon size={16} color='midnight80' />
                </Button>
            </Flex>

            {isFetching || lines.length ? (
                <Flex direction='column' spacing='16'>
                    <Box width='100%' overflowX='auto' overflowY='auto'>
                        <Table
                            hoverable={false}
                            headerColor='transparent'
                            columns={columnDefinitions}
                            data={lines}
                            hideEmptyColumns
                        />
                    </Box>

                    <Footer request={request} matchingLines={matchingLinesInfos} lineNumber={lineNumber} />
                </Flex>
            ) : (
                <Flex justifyContent='center'>
                    <NoContent icon={<EmptyBoxImage />} heading={messages.emptyMatching} />
                </Flex>
            )}
        </Box>
    );
});

Content.displayName = 'Content';
